import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_ZOOM = 0.02; // ~2km span for bbox

type MapPickerProps = {
    latitude: number | null;
    longitude: number | null;
    onLocationChange: (lat: number, lng: number) => void;
};

export default function MapPicker({ latitude, longitude, onLocationChange }: MapPickerProps) {
    const { t } = useTranslation();
    const [latInput, setLatInput] = useState('');
    const [lngInput, setLngInput] = useState('');

    useEffect(() => {
        setLatInput(latitude != null ? String(latitude) : '');
        setLngInput(longitude != null ? String(longitude) : '');
    }, [latitude, longitude]);

    const lat = latitude ?? null;
    const lng = longitude ?? null;
    const hasCoords = lat != null && lng != null && !Number.isNaN(Number(latInput)) && !Number.isNaN(Number(lngInput));

    const handleBothBlur = () => {
        const latNum = parseFloat(latInput);
        const lngNum = parseFloat(lngInput);
        if (!Number.isNaN(latNum) && latNum >= -90 && latNum <= 90 &&
            !Number.isNaN(lngNum) && lngNum >= -180 && lngNum <= 180) {
            onLocationChange(latNum, lngNum);
        }
    };

    const displayLat = hasCoords ? (typeof lat === 'number' ? lat : parseFloat(String(lat))) : 0;
    const displayLng = hasCoords ? (typeof lng === 'number' ? lng : parseFloat(String(lng))) : 0;
    const bbox = `${displayLng - DEFAULT_ZOOM},${displayLat - DEFAULT_ZOOM},${displayLng + DEFAULT_ZOOM},${displayLat + DEFAULT_ZOOM}`;
    const embedUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(bbox)}&layer=mapnik&marker=${displayLat},${displayLng}`;

    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t("Latitude")}</label>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={latInput}
                        onChange={(e) => setLatInput(e.target.value)}
                        onBlur={handleBothBlur}
                        placeholder={t("e.g. 35.6892")}
                        className="py-2 px-3 block w-full border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t("Longitude")}</label>
                    <input
                        type="text"
                        inputMode="decimal"
                        value={lngInput}
                        onChange={(e) => setLngInput(e.target.value)}
                        onBlur={handleBothBlur}
                        placeholder={t("e.g. 51.3890")}
                        className="py-2 px-3 block w-full border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                </div>
            </div>
            {hasCoords && (
                <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800">
                    <iframe
                        title={t("Map Location")}
                        src={embedUrl}
                        className="w-full h-[280px] border-0"
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            )}
        </div>
    );
}
